module Sm.Model.Tracker

open System
open MobF

open Sm.Service
open Sm.Service.Tracking
open Sm.Service.Repository
open Sm.Service.Storage

open Sm.Model.RealtimeQuery

type Buffer = {
    Current: Map<Range, Seconds>
    Pending: Map<Range, Seconds>
    Seen: Set<int64>
}

type State = {
    Query: RealtimeQuery.Type<DateRange, Buffer>
}

type Msg =
    | Load of DateRange
    | Update of Tracking.Adjustment
    | Apply

type IComputed =
    abstract Sample: (Range -> Seconds)
    abstract Pending: Set<int64>

type Type = Model<State, Msg, IComputed>

let create (env: Environment) repository : Type =
    let init () =
        {
            Query = RealtimeQuery.create {
                Empty = { 
                    Current = Map.empty
                    Pending = Map.empty
                    Seen = Set.empty 
                }
                Repository = repository
                Execute = (fun range -> promise {
                    let! initial = repository.QueryTracking(range)
                    return {
                        Current = initial
                        Pending = Map.empty
                        Seen = Set.empty
                    }
                })
                Merge = (fun range e buffer ->
                    let adjustment = e.Update.Target

                    match adjustment.Field, adjustment.Value with
                    | Document.TrackerField, Some value ->
                        let records = 
                            value
                            |> Data.parse
                            |> Seq.filter (buffer.Seen.Contains >> not)

                        let data =
                            records
                            |> Data.decode
                            |> Seq.filter (fun (r, _) -> range |> DateRange.contains r.Date)

                        let nextCurrent = buffer.Current |> Store.apply data
                        let nextSeen = buffer.Seen |> Set.union (Set records)

                        { buffer with Current = nextCurrent; Seen = nextSeen }
                    | _ ->
                        buffer
                )
            }
        }

    let update (state, _, computed: IComputed) msg =
        let mapBuffer f =
            match state.Query.State.Operation with
            | Ready (range, Ok buffer) ->
                let nextBuffer = f range buffer
                let nextOp = Ready (range, Ok nextBuffer)
                state.Query.Post(RealtimeQuery.Update nextOp)
            | _ -> ()

        match msg with
        | Load range ->
            state.Query.Post(Execute range)
            state

        | Update adjustment ->
            mapBuffer (fun range buffer ->
                let instant = DateTime.Now
                if range |> DateRange.contains (DateOnly.FromDateTime instant) then
                    let centile =  Range.centileOf instant
                    let nextPending = (centile, buffer.Pending) ||> Store.update adjustment secondsPerCentile

                    { buffer with Pending = nextPending }
                else
                    buffer
            )
            state

        | Apply ->
            mapBuffer (fun _ buffer ->
                let nextSeen = buffer.Seen |> Set.union computed.Pending
                let nextCurrent = buffer.Current |> Store.apply (buffer.Pending |> Map.toSeq)

                { buffer with Current = nextCurrent; Pending = Map.empty; Seen = nextSeen }
            )
            state

    let compute state =
        { new IComputed with
            member _.Sample =
                fun range ->
                    let buffer = state.Query.Computed.Result

                    let read s = 
                        s
                        |> Map.tryFind range
                        |> Option.defaultValue Seconds.Zero

                    range |> Range.combine (read buffer.Current) (read buffer.Pending)

            member _.Pending =
                state.Query.Computed.Result.Pending
                |> Map.toSeq
                |> Data.encode env.ClientId
                |> Set
        }

    Model.useInit init
    |> Model.andUpdateWithComputed update
    |> Model.createWithComputed compute
